/**
 * /admin/ui: staff-only gallery of every shared component in every state, for visual QA of the design system.
 * Hover / focus / pressed are painted with `data-force` (ds.css) so all states sit side by side; everything else
 * is the real component. `?theme=dark` renders the page in the dark theme. Not in the sidebar on purpose.
 */
import {
  Badge,
  BADGE_TONES,
  Banner,
  Button,
  Combobox,
  ConfirmDialog,
  DataTable,
  DatePicker,
  DateRangePicker,
  DefinitionList,
  Disclosure,
  Drawer,
  ErrorState,
  FilterToolbar,
  InlineError,
  Menu,
  Modal,
  MoreIcon,
  MultiSelect,
  NumberField,
  PageHeader,
  PhoneField,
  SearchInput,
  Section,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  STATUS_MAPS,
  TabPanel,
  Tabs,
  TextArea,
  TextField,
  Toggle,
  US_STATES,
  notify,
  type Column,
  type DateRange,
  type SortState,
  type StatusDomain,
} from "@flexfreight/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

const STATES = ["default", "hover", "focus", "pressed", "disabled", "loading"] as const;
type ForcedState = (typeof STATES)[number];

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ffa-ui__row">
      <span className="ffa-ui__label">{label}</span>
      <div className="ffa-ui__cells">{children}</div>
    </div>
  );
}
function Cell({ caption, children }: { caption?: string; children: ReactNode }) {
  return (
    <figure className="ffa-ui__cell">
      {children}
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}
const force = (s: ForcedState) => (s === "hover" || s === "focus" || s === "pressed" ? s : undefined);

type Shipment = { id: string; bol: string; customer: string; lane: string; status: string; charge: number; margin: number };
const SHIPMENTS: Shipment[] = [
  { id: "1", bol: "FF-104233", customer: "Acme Warehouse", lane: "Chicago, IL → Dallas, TX", status: "in_transit", charge: 41400, margin: 5400 },
  { id: "2", bol: "FF-104234", customer: "Bolt Logistics and Distribution Services of the Greater Midwest", lane: "Newark, NJ → Atlanta, GA", status: "exception", charge: 128830, margin: 15210 },
  { id: "3", bol: "FF-104235", customer: "Cascade Outdoor Goods", lane: "Seattle, WA → Portland, OR", status: "delivered", charge: 23050, margin: 2890 },
  { id: "4", bol: "FF-104236", customer: "Prairie Ag Equipment", lane: "Omaha, NE → Denver, CO", status: "booked", charge: 87300, margin: 9900 },
];
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export function UiGallery() {
  const [params] = useSearchParams();
  const theme = params.get("theme");
  useEffect(() => {
    if (theme !== "dark" && theme !== "light") return undefined;
    const el = document.documentElement;
    const before = el.getAttribute("data-theme");
    el.setAttribute("data-theme", theme);
    return () => { if (before) el.setAttribute("data-theme", before); };
  }, [theme]);

  return (
    <section className="ffa-page ffa-ui">
      <PageHeader eyebrow="Admin Center" title="UI gallery" subtitle="Every shared component in every state, for visual QA. Hover, focus and pressed are painted so they sit side by side; everything else is the live component." meta={<><span>Tokens: <strong>tokens.css v5</strong></span><span>Styles: <strong>controls.css · ds.css</strong></span><span>Add <strong>?theme=dark</strong> for the dark theme</span></>} />
      <ButtonsSection />
      <InputsSection />
      <SelectionSection />
      <FeedbackSection />
      <BadgesSection />
      <ToolbarSection />
      <TableSection />
      <LayoutSection />
      <MotionSection />
    </section>
  );
}

function ButtonsSection() {
  return (
    <Section title="Buttons" description="Four variants, three sizes. One primary per view. Press scales to 0.96 over 120ms; loading keeps the width.">
      {(["primary", "secondary", "ghost", "destructive"] as const).map((v) => (
        <Row key={v} label={v}>
          {STATES.map((s) => (
            <Cell key={s} caption={s}>
              <Button variant={v} data-force={force(s)} disabled={s === "disabled"} loading={s === "loading"}>
                {v === "destructive" ? "Delete" : v === "primary" ? "Book shipment" : "Export"}
              </Button>
            </Cell>
          ))}
        </Row>
      ))}
      <Row label="sizes">
        <Cell caption="sm"><Button size="sm">Small</Button></Cell>
        <Cell caption="md"><Button>Medium</Button></Cell>
        <Cell caption="lg (field height)"><Button size="lg" variant="primary">Large</Button></Cell>
        <Cell caption="menu"><Menu trigger="Actions" items={[{ label: "Edit" }, { label: "Duplicate" }, { type: "separator" }, { label: "Archive", danger: true }]} /></Cell>
        <Cell caption="icon menu"><Menu look="icon" trigger={<MoreIcon />} triggerLabel="More actions" items={[{ label: "Copy BOL" }, { label: "Open tracking" }]} /></Cell>
      </Row>
    </Section>
  );
}

function InputsSection() {
  const [weight, setWeight] = useState<number | null>(1200);
  const [phone, setPhone] = useState("(732) 555-0100");
  return (
    <Section title="Inputs" description="Integrated label: inside the box when empty, a small persistent label when focused or filled. One thin focus ring; errors are red text plus a red border, never color alone.">
      {(["md", "sm"] as const).map((size) => (
        <Row key={size} label={`TextField ${size}`}>
          <Cell caption="empty"><TextField size={size} label="Company" /></Cell>
          <Cell caption="hover"><HoverBox><TextField size={size} label="Company" /></HoverBox></Cell>
          <Cell caption="focus (example shows)"><FocusBox><TextField size={size} label="Company" example="Acme Freight" /></FocusBox></Cell>
          <Cell caption="filled"><TextField size={size} label="Company" defaultValue="Cascade Outdoor Goods" /></Cell>
          <Cell caption="error"><TextField size={size} label="ZIP" defaultValue="0870" error="Enter a 5-digit ZIP." /></Cell>
          <Cell caption="disabled"><TextField size={size} label="Account ID" defaultValue="FF-00231" disabled /></Cell>
          <Cell caption="optional + hint"><TextField size={size} label="Reference" optional hint="Shown on the BOL." /></Cell>
        </Row>
      ))}
      <Row label="number / phone / prefix">
        <Cell caption="NumberField, unit"><NumberField label="Total weight" unit="lb" unitLabel="pounds" value={weight} onChange={setWeight} min={0} max={20000} step={10} /></Cell>
        <Cell caption="NumberField, empty"><NumberField label="Pallets" value={null} onChange={() => {}} min={1} precision={0} /></Cell>
        <Cell caption="PhoneField"><PhoneField label="Support phone" value={phone} onChange={setPhone} /></Cell>
        <Cell caption="prefix"><TextField label="Declared value" prefix="$" defaultValue="2,500" /></Cell>
        <Cell caption="suffix error"><TextField label="Length" suffix="in" defaultValue="400" error="Max 96 in for this trailer." /></Cell>
      </Row>
      <Row label="TextArea / Toggle">
        <Cell caption="TextArea"><TextArea label="Special instructions" optional hint="Shown to the driver." /></Cell>
        <Cell caption="check"><Toggle label="Stackable" checked onChange={() => {}} /></Cell>
        <Cell caption="check off"><Toggle label="Hazmat" checked={false} onChange={() => {}} /></Cell>
        <Cell caption="switch"><Toggle look="switch" label="Shipment exception" description="Email when a carrier reports one." checked onChange={() => {}} /></Cell>
        <Cell caption="disabled"><Toggle label="Liftgate" checked={false} onChange={() => {}} disabled /></Cell>
      </Row>
    </Section>
  );
}
/** Wrappers that paint a forced state on the field box inside them. */
function HoverBox({ children }: { children: ReactNode }) {
  return <div className="ffa-ui__force" data-force-box="hover">{children}</div>;
}
function FocusBox({ children }: { children: ReactNode }) {
  return <div className="ffa-ui__force" data-force-box="focus">{children}</div>;
}
/** Applies data-force to the .ffc-box inside each forced wrapper (after mount; the gallery only). */
function useForcedBoxes() {
  useEffect(() => {
    document.querySelectorAll<HTMLElement>("[data-force-box]").forEach((w) => {
      const box = w.querySelector<HTMLElement>(".ffc-box");
      if (box) box.setAttribute("data-force", w.dataset.forceBox ?? "");
    });
  });
}

function SelectionSection() {
  useForcedBoxes();
  const [policy, setPolicy] = useState<string>("approval");
  const [state, setState] = useState<string | null>("NJ");
  const [carriers, setCarriers] = useState<string[]>(["xpo", "odfl"]);
  const [range, setRange] = useState<DateRange>({ start: "2026-10-01", end: "2026-10-14" });
  const [pickup, setPickup] = useState<string | null>(null);
  const load = useMemo(() => async (q: string) => [{ value: "chi", label: `Chicago, IL 60607`, detail: `matches "${q}"` }, { value: "chd", label: "Chandler, AZ 85224" }], []);
  const carrierOpts = [{ value: "xpo", label: "XPO" }, { value: "odfl", label: "Old Dominion" }, { value: "estes", label: "Estes" }, { value: "saia", label: "Saia" }];
  return (
    <Section title="Selection" description="Select, Combobox, MultiSelect, Menu, DatePicker and DateRangePicker share the menu surface and one opening motion (scale 0.97 + fade, 180ms in, 120ms out). Open any of them; every one works from the keyboard.">
      <Row label="Select">
        <Cell caption="empty"><Select label="Location type" value={null} onChange={() => {}} options={[{ value: "biz", label: "Business" }]} /></Cell>
        <Cell caption="focus"><FocusBox><Select label="Location type" value={null} onChange={() => {}} options={[{ value: "biz", label: "Business" }]} /></FocusBox></Cell>
        <Cell caption="filled"><Select label="Who can open an account" value={policy} onChange={setPolicy} options={[{ value: "open", label: "Open" }, { value: "approval", label: "Review new accounts" }, { value: "invite", label: "Invitation only" }]} /></Cell>
        <Cell caption="error"><Select label="Freight class" value={null} onChange={() => {}} options={[{ value: "70", label: "70" }]} error="Choose a class." /></Cell>
        <Cell caption="disabled"><Select label="Carrier" value="xpo" onChange={() => {}} options={[{ value: "xpo", label: "XPO" }]} disabled /></Cell>
        <Cell caption="sm"><Select size="sm" label="Role" value="ops" onChange={() => {}} options={[{ value: "ops", label: "Operations" }]} /></Cell>
        <Cell caption="toolbar"><Select variant="toolbar" label="Type" value="all" onChange={() => {}} options={[{ value: "all", label: "All" }]} /></Cell>
        <Cell caption="toolbar active"><Select variant="toolbar" label="Type" value="p" onChange={() => {}} active options={[{ value: "p", label: "Prospects" }]} /></Cell>
      </Row>
      <Row label="Combobox / Multi">
        <Cell caption="searchable picker"><Combobox label="State" options={US_STATES.map((s) => ({ ...s, detail: s.value }))} value={state} onChange={(v) => setState(v)} clearable /></Cell>
        <Cell caption="async search"><Combobox label="Ship from" placeholder="City, ZIP or saved address" load={load} onSelect={() => {}} debounceMs={0} /></Cell>
        <Cell caption="MultiSelect field"><MultiSelect label="Carriers" options={carrierOpts} value={carriers} onChange={setCarriers} /></Cell>
        <Cell caption="MultiSelect toolbar"><MultiSelect variant="toolbar" label="Carrier" options={carrierOpts} value={carriers} onChange={setCarriers} /></Cell>
        <Cell caption="MultiSelect empty"><MultiSelect variant="toolbar" label="Carrier" options={carrierOpts} value={[]} onChange={() => {}} /></Cell>
      </Row>
      <Row label="dates">
        <Cell caption="DatePicker field"><DatePicker variant="field" rule="any" label="Due date" value={pickup} onChange={setPickup} optional /></Cell>
        <Cell caption="DateRangePicker field"><DateRangePicker label="Created" value={range} onChange={setRange} /></Cell>
        <Cell caption="DateRangePicker toolbar"><DateRangePicker variant="toolbar" label="Created" value={range} onChange={setRange} /></Cell>
        <Cell caption="toolbar empty"><DateRangePicker variant="toolbar" label="Delivered" value={{ start: null, end: null }} onChange={() => {}} /></Cell>
      </Row>
      <Row label="menu surface">
        <Cell caption="listbox: highlighted + selected">
          <div className="ffc-menu ffx-menu-sample" aria-hidden="true">
            <div className="ffc-opt" data-highlighted=""><span className="ffc-opt__text">Old Dominion</span><span className="ffc-opt__meta">ODFL</span></div>
            <div className="ffc-opt" data-selected=""><span className="ffc-opt__text">XPO</span><span className="ffc-opt__check"><svg className="ffc-check" viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg></span></div>
            <div className="ffc-opt" data-disabled=""><span className="ffc-opt__text">Saia (no lane)</span></div>
          </div>
        </Cell>
        <Cell caption="menu: danger highlighted">
          <div className="ffc-menu ffx-menu ffx-menu-sample" aria-hidden="true">
            <div className="ffx-menu__label">Shipment</div>
            <div className="ffc-opt ffx-menu__item"><span className="ffc-opt__text">Copy BOL</span><kbd className="ffc-opt__meta">⌘C</kbd></div>
            <div className="ffc-sep" />
            <div className="ffc-opt ffx-menu__item ffx-menu__item--danger" data-highlighted=""><span className="ffc-opt__text">Cancel shipment</span></div>
          </div>
        </Cell>
        <Cell caption="multi: checked rows">
          <div className="ffc-menu ffx-menu-sample" aria-hidden="true">
            <div className="ffc-opt" data-selected=""><span className="ffx-multi__box"><svg className="ffc-check" viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg></span><span className="ffc-opt__text">XPO</span></div>
            <div className="ffc-opt" data-highlighted=""><span className="ffx-multi__box"><svg className="ffc-check" viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg></span><span className="ffc-opt__text">Estes</span></div>
            <div className="ffx-multi__foot"><span className="ffx-multi__n">1 selected</span><span className="ffc-link">Clear</span></div>
          </div>
        </Cell>
      </Row>
    </Section>
  );
}

function FeedbackSection() {
  const [confirm, setConfirm] = useState<null | "danger" | "plain">(null);
  const [modal, setModal] = useState(false);
  const [drawer, setDrawer] = useState(false);
  return (
    <Section title="Feedback" description="Banner (page or section), ErrorState (a region that failed), InlineError, ConfirmDialog (never window.confirm), Modal, Drawer and toasts.">
      <div className="ffa-ui__stack">
        <Banner tone="info" title="Carrier rates are in mock mode">Quotes use simulated prices until live credentials are added.</Banner>
        <Banner tone="success" title="Zoho is syncing">Invoices post to Zoho Books within a minute.</Banner>
        <Banner tone="warning" title="Old Dominion bills the shipper" actions={<Button size="sm">Add billing address</Button>}>Until FlexFreight's billing address is complete.</Banner>
        <Banner tone="danger" title="Zoho sync failed" actions={<Button size="sm">Retry</Button>} onDismiss={() => {}}>The token expired at 3:10 PM. Reconnect to resume.</Banner>
      </div>
      <Row label="states">
        <Cell caption="ErrorState"><ErrorState title="Couldn't load shipments" message="The server didn't answer. Check the connection." onRetry={() => {}} /></Cell>
        <Cell caption="InlineError"><InlineError>Couldn't save the note. Try again.</InlineError></Cell>
      </Row>
      <Row label="overlays">
        <Cell caption="ConfirmDialog (danger)"><Button variant="destructive" onClick={() => setConfirm("danger")}>Remove credentials…</Button></Cell>
        <Cell caption="ConfirmDialog"><Button onClick={() => setConfirm("plain")}>End view…</Button></Cell>
        <Cell caption="Modal"><Button onClick={() => setModal(true)}>Open modal</Button></Cell>
        <Cell caption="Drawer"><Button onClick={() => setDrawer(true)}>Open drawer</Button></Cell>
        <Cell caption="toasts">
          <span className="ffa-ui__inline">
            <Button size="sm" onClick={() => notify.success("Shipment booked", "BOL FF-104233")}>Success</Button>
            <Button size="sm" onClick={() => notify.warning("Rate expires soon")}>Warning</Button>
            <Button size="sm" onClick={() => notify.error("Booking failed", "XPO rejected the pickup date.")}>Error</Button>
          </span>
        </Cell>
      </Row>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm === "danger" ? "Remove XPO credentials?" : "End this customer view?"}
        body={confirm === "danger" ? "Quoting with this carrier goes back to mock rates until new credentials are added." : "The portal tab is signed out on its next click."}
        confirmLabel={confirm === "danger" ? "Remove" : "End view"}
        danger={confirm === "danger"}
        onConfirm={() => setConfirm(null)}
        onCancel={() => setConfirm(null)}
      />
      <Modal open={modal} onClose={() => setModal(false)} title="Edit customer" description="Changes are saved to the audit log." footer={<><Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button><Button variant="primary" onClick={() => setModal(false)}>Save</Button></>}>
        <div className="ffa-form"><TextField label="Company" defaultValue="Acme Warehouse" /><PhoneField label="Phone" value="(732) 555-0100" onChange={() => {}} /></div>
      </Modal>
      <Drawer open={drawer} onClose={() => setDrawer(false)} title="Shipment FF-104233" description="Chicago, IL → Dallas, TX" footer={<Button variant="primary" onClick={() => setDrawer(false)}>Done</Button>}>
        <DefinitionList items={[{ term: "Status", detail: <StatusBadge domain="shipment" value="in_transit" /> }, { term: "Carrier", detail: "XPO" }, { term: "Charge", detail: "$414.00" }]} />
      </Drawer>
    </Section>
  );
}

function BadgesSection() {
  const domains = Object.keys(STATUS_MAPS) as StatusDomain[];
  return (
    <Section title="Badges and status" description="One Badge, six tones, two sizes. Every app status maps to a tone in one file (status-map.ts); text always carries the meaning.">
      {(["md", "sm"] as const).map((size) => (
        <Row key={size} label={`tones ${size}`}>
          {BADGE_TONES.map((t) => <Cell key={t} caption={t}><Badge tone={t} size={size}>{t[0]!.toUpperCase() + t.slice(1)}</Badge></Cell>)}
          <Cell caption="no dot"><Badge tone="warning" size={size} dot={false}>Prospect</Badge></Cell>
          <Cell caption="live"><Badge tone="info" size={size} live>In transit</Badge></Cell>
        </Row>
      ))}
      {domains.map((d) => (
        <Row key={d} label={d}>
          {Object.keys(STATUS_MAPS[d]).map((v) => <StatusBadge key={v} domain={d} value={v} size="sm" />)}
        </Row>
      ))}
    </Section>
  );
}

function ToolbarSection() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string[]>([]);
  const [carrier, setCarrier] = useState("all");
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [mode, setMode] = useState("all");
  const [view, setView] = useState<string | null>("open");
  const any = q !== "" || status.length > 0 || carrier !== "all" || !!range.start || mode !== "all";
  return (
    <Section title="FilterToolbar" description="Exactly one row: search, the most important filters, then More filters (with an active count) for the rest. Narrow the window: low-priority filters move into More filters instead of wrapping. State lives in the URL on real pages (useUrlFilters).">
      <FilterToolbar
        label="Shipment filters"
        search={<SearchInput label="Search BOL, PRO, PO, city" value={q} onChange={setQ} />}
        items={[
          { key: "status", priority: 1, active: status.length > 0, inline: <MultiSelect variant="toolbar" label="Status" value={status} onChange={setStatus} options={Object.entries(STATUS_MAPS.shipment).map(([value, s]) => ({ value, label: s.label }))} /> },
          { key: "carrier", priority: 2, active: carrier !== "all", inline: <Select variant="toolbar" label="Carrier" value={carrier} onChange={setCarrier} options={[{ value: "all", label: "All" }, { value: "xpo", label: "XPO" }, { value: "odfl", label: "Old Dominion" }]} /> },
          { key: "created", priority: 3, active: !!range.start, inline: <DateRangePicker variant="toolbar" label="Created" value={range} onChange={setRange} /> },
          { key: "mode", priority: 4, active: mode !== "all", inline: <Select variant="toolbar" label="Mode" value={mode} onChange={setMode} options={[{ value: "all", label: "All" }, { value: "ltl", label: "LTL" }, { value: "ftl", label: "Truckload" }]} /> },
        ]}
        extra={[{ key: "flags", group: "Flags", panel: <Toggle label="Guaranteed only" checked={false} onChange={() => {}} /> }]}
        anyActive={any}
        showReset
        onReset={() => { setQ(""); setStatus([]); setCarrier("all"); setRange({ start: null, end: null }); setMode("all"); }}
        views={{ items: [{ id: "open", label: "Open shipments" }, { id: "exceptions", label: "Exceptions" }], value: view, onChange: setView, onSave: () => notify.info("Save view", "The page asks for a name.") }}
        meta="373 shipments"
      />
    </Section>
  );
}

function TableSection() {
  const [sort, setSort] = useState<SortState>({ key: "charge", dir: "desc" });
  const [sel, setSel] = useState<Set<string>>(new Set(["2"]));
  const [page, setPage] = useState(0);
  const rows = useMemo(() => {
    const out = [...SHIPMENTS];
    if (sort) out.sort((a, b) => { const k = sort.key as keyof Shipment; const r = a[k] < b[k] ? -1 : a[k] > b[k] ? 1 : 0; return sort.dir === "asc" ? r : -r; });
    return out;
  }, [sort]);
  const columns: Column<Shipment>[] = [
    { key: "bol", header: "BOL", primary: true, sortable: true, width: "9rem", render: (r) => <a href={`#${r.bol}`} onClick={(e) => e.preventDefault()}>{r.bol}</a> },
    { key: "customer", header: "Customer", sortable: true, truncate: true, render: (r) => r.customer },
    { key: "lane", header: "Lane", truncate: true, mobile: "hide", render: (r) => r.lane },
    { key: "status", header: "Status", width: "9rem", render: (r) => <StatusBadge domain="shipment" value={r.status} /> },
    { key: "charge", header: "Charge", numeric: true, sortable: true, width: "8rem", render: (r) => usd.format(r.charge / 100) },
    { key: "margin", header: "Margin", numeric: true, sortable: true, width: "7.5rem", hideable: true, render: (r) => usd.format(r.margin / 100) },
  ];
  return (
    <Section title="DataTable" description="Sticky header, sortable columns, right-aligned tabular numbers, truncation with a tooltip only when cut, selection with a bulk bar, row click, column visibility, pagination. Below 40rem of container width each row becomes a labelled card.">
      <DataTable
        caption="Shipments"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={setSort}
        selection={{ selected: sel, onChange: setSel, rowLabel: (r) => r.bol, actions: (keys, clear) => <><Button size="sm" onClick={() => { notify.success(`${keys.length} exported`); clear(); }}>Export</Button><Button size="sm" variant="ghost">Assign</Button></> }}
        onRowClick={(r) => notify.info(`Open ${r.bol}`)}
        rowTone={(r) => (r.status === "exception" ? "danger" : null)}
        columnsMenu
        pagination={{ page, pageSize: 4, total: 37, onPageChange: setPage }}
      />
      <Row label="states">
        <Cell caption="loading (first load)"><div className="ffa-ui__wide"><DataTable caption="Loading" columns={columns.slice(0, 4)} rows={[]} rowKey={(r) => r.id} loading pagination={{ page: 0, pageSize: 3, onPageChange: () => {} }} /></div></Cell>
        <Cell caption="empty"><div className="ffa-ui__wide"><DataTable caption="Empty" columns={columns.slice(0, 4)} rows={[]} rowKey={(r) => r.id} empty="No shipments match these filters." /></div></Cell>
        <Cell caption="error"><div className="ffa-ui__wide"><DataTable caption="Shipments" columns={columns.slice(0, 4)} rows={[]} rowKey={(r) => r.id} error={{ message: "The server didn't answer.", onRetry: () => {} }} /></div></Cell>
        <Cell caption="compact + hover row"><div className="ffa-ui__wide ffa-ui__hoverfirst"><DataTable caption="Compact" density="compact" columns={columns.slice(0, 5)} rows={SHIPMENTS.slice(0, 3)} rowKey={(r) => r.id} /></div></Cell>
      </Row>
    </Section>
  );
}

function LayoutSection() {
  const [tab, setTab] = useState("overview");
  const [seg, setSeg] = useState<"comfortable" | "compact">("comfortable");
  return (
    <Section title="Layout" description="Tabs (arrows move, Home/End jump), SegmentedControl, Skeleton, Section, PageHeader, DetailLayout pieces, Disclosure (expandable).">
      <Tabs idBase="ui-tabs" label="Sample sections" value={tab} onChange={setTab} tabs={[{ value: "overview", label: "Overview" }, { value: "users", label: "Users", count: 3 }, { value: "shipments", label: "Shipments", count: 128 }, { value: "notes", label: "Notes" }]} />
      <TabPanel idBase="ui-tabs" value={tab}><p className="ffa-muted">Panel: {tab}</p></TabPanel>
      <Row label="tab states">
        <Cell caption="hover"><button type="button" role="tab" aria-selected="false" className="ffx-tab" data-force="hover">Quotes</button></Cell>
        <Cell caption="focus"><button type="button" role="tab" aria-selected="false" className="ffx-tab" data-force="focus">Quotes</button></Cell>
        <Cell caption="selected"><button type="button" role="tab" aria-selected="true" className="ffx-tab">Quotes</button></Cell>
        <Cell caption="SegmentedControl"><SegmentedControl label="Density" value={seg} onChange={setSeg} options={[{ value: "comfortable", label: "Comfortable" }, { value: "compact", label: "Compact" }]} /></Cell>
      </Row>
      <Row label="loading">
        <Cell caption="Skeleton"><span className="ffa-ui__inline"><Skeleton width="2.5rem" height="2.5rem" radius="50%" /><Skeleton width="10rem" /></span></Cell>
        <Cell caption="SkeletonText"><div className="ffa-ui__narrow"><SkeletonText lines={3} /></div></Cell>
      </Row>
      <div className="ffa-ui__frame">
        <PageHeader back={<a href="#back" className="ffa-back" onClick={(e) => e.preventDefault()}>← Customers</a>} eyebrow="Customer" title="Acme Warehouse" badges={<Badge tone="warning" dot={false}>Prospect</Badge>} subtitle="Customer since Sep 1, 2026 · Net 30 · $5,000.00 credit limit" secondaryActions={<Button>View as customer</Button>} primaryAction={<Button variant="primary">New quote</Button>} />
      </div>
      <div className="ffa-ui__grid2">
        <Section title="Account" level={3}>
          <DefinitionList items={[{ term: "Terms", detail: "Net 30" }, { term: "Users", detail: 3 }, { term: "Phone", detail: "(732) 555-0100" }, { term: "Carrier accounts", detail: "Uses FlexFreight rates" }]} />
        </Section>
        <Section title="Expandables" level={3}>
          <Disclosure summary="Why is this rate higher?" defaultOpen>Liftgate and residential delivery add $96.00 at XPO.</Disclosure>
          <Disclosure summary="Carrier details">SCAC: ODFL · Terminal: Elizabeth, NJ</Disclosure>
        </Section>
      </div>
    </Section>
  );
}

function MotionSection() {
  const tokens = [
    ["--ff-motion-fast", "120ms", "press feedback, hover, toggles, tab indicator"],
    ["--ff-motion-base", "180ms", "dropdowns, menus, popovers, expandables"],
    ["--ff-motion-slow", "280ms", "modals, drawers, sheets, toasts"],
    ["--ff-motion-exit", "120ms", "every exit"],
    ["--ff-ease-standard", "cubic-bezier(0.2, 0, 0, 1)", "state changes on screen"],
    ["--ff-ease-emphasized", "cubic-bezier(0.32, 0.72, 0, 1)", "entering surfaces"],
    ["--ff-ease-exit-fast", "cubic-bezier(0.23, 1, 0.32, 1)", "leaving surfaces"],
  ];
  return (
    <Section title="Motion tokens" description="Transform and opacity only, transitions (interruptible), gated on prefers-reduced-motion: no-preference. Under reduced motion, movement is removed and a short fade remains." flush>
      <DataTable caption="Motion tokens" maxHeight="none" columns={[{ key: "t", header: "Token", primary: true, render: (r: string[]) => <code>{r[0]}</code> }, { key: "v", header: "Value", render: (r: string[]) => <code>{r[1]}</code> }, { key: "u", header: "Used for", render: (r: string[]) => r[2] }]} rows={tokens} rowKey={(r) => r[0]!} density="compact" />
    </Section>
  );
}
